import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { supabase, isSupabaseConfigured } from '../supabaseClient';

const { FiSettings, FiCpu, FiCheck, FiAlertCircle, FiSave, FiExternalLink } = FiIcons;

export default function Admin() {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('integrations');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Integrations Config State
  const [config, setConfig] = useState({
    googleCalendar: {
      clientId: '',
      apiKey: '',
      calendarId: ''
    }
  });

  useEffect(() => {
    if (activeTab === 'integrations') {
        loadSettings();
    }
  }, [activeTab, user]);

  const loadSettings = async () => {
    try {
      if (isSupabaseConfigured() && user) {
        setLoading(true);
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'integrations')
          .maybeSingle();

        if (error) {
            console.error('Error loading settings:', error);
            setError('Konnte Einstellungen nicht laden: ' + error.message);
        } else if (data) {
            setConfig(prev => ({
                ...prev,
                googleCalendar: {
                    clientId: data.value?.googleCalendar?.clientId || '',
                    apiKey: data.value?.googleCalendar?.apiKey || '',
                    calendarId: data.value?.googleCalendar?.calendarId || ''
                },
                ...data.value
            }));
        }
      }
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('Ladefehler: ' + err.message);
    } finally {
        setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isSupabaseConfigured() && user) {
        const { error } = await supabase
          .from('app_settings')
          .upsert({ 
            key: 'integrations', 
            value: config, 
            updated_at: new Date().toISOString(),
            updated_by: user.id
          }, {
            onConflict: 'key'
          });
          
        if (error) throw new Error(error.message);
        setSuccess('Einstellungen erfolgreich gespeichert!');
        await loadSettings(); 
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('Fehler beim Speichern: ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-red-600">
        <SafeIcon icon={FiAlertCircle} className="w-12 h-12 mx-auto mb-4" />
        <h2 className="text-xl font-bold">Zugriff verweigert</h2>
        <p>Nur Administratoren haben Zugriff auf diesen Bereich.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Konsole</h1>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-lg"
          >
            <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2" />
            {success}
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center bg-red-100 text-red-700 px-4 py-2 rounded-lg"
          >
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2" />
            {error}
          </motion.div>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Navigation */}
        <nav className="w-full md:w-64 space-y-2">
          <button
            onClick={() => setActiveTab('integrations')}
            className={`w-full flex items-center px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'integrations' 
                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
                : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'
            }`}
          >
            <SafeIcon icon={FiCpu} className="w-5 h-5 mr-3" />
            Integrationen
          </button>
          
          <div className="pt-8 text-xs text-gray-500 px-4 dark:text-gray-500">
             <p>Hinweis: Die Benutzerverwaltung befindet sich in den "Einstellungen".</p>
             <a href="/#/settings" className="text-blue-600 hover:underline flex items-center mt-2 dark:text-blue-400">
                <SafeIcon icon={FiExternalLink} className="w-3 h-3 mr-1" />
                Zu den Einstellungen
             </a>
          </div>
        </nav>

        {/* Content Area */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6 dark:bg-gray-800 dark:border-gray-700">
          
          {activeTab === 'integrations' && (
            <form onSubmit={handleSave} className="space-y-6">
              <h2 className="text-xl font-semibold mb-4 dark:text-white">Externe Dienste</h2>
              
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center dark:text-white">
                  <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2" />
                  Google Calendar Integration
                </h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Client ID</label>
                    <input
                      type="text"
                      value={config.googleCalendar?.clientId || ''}
                      onChange={(e) => setConfig({
                        ...config,
                        googleCalendar: { ...config.googleCalendar, clientId: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      placeholder="Google OAuth Client ID"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">API Key</label>
                    <input
                      type="password"
                      value={config.googleCalendar?.apiKey || ''}
                      onChange={(e) => setConfig({
                        ...config,
                        googleCalendar: { ...config.googleCalendar, apiKey: e.target.value }
                      })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                      placeholder="Google API Key"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {loading ? 'Speichere...' : 'Speichern'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}